import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

export interface PlanLesson { lesson_id: number; title: string; learning_objective?: string; core_concepts?: string[]; passing_score?: number }
export interface PlanModule { module_id: number; title: string; lessons: PlanLesson[] }

export interface QuizEvidence { best: number; attempts: number; lastAt: string | null; durationSeconds: number | null }
export interface LogEvidence { completedAt: string | null; strongAreas: string[]; weakAreas: string[] }

export interface RecordLesson {
  modulePos: number; moduleTitle: string; lessonPos: number
  title: string; objective: string | null; conceptCount: number
  quiz?: QuizEvidence; log?: LogEvidence; completed: boolean
}

export interface EssayEvidence { modulePos: number; score: number | null; maxScore: number | null; feedback: string | null; submittedAt: string | null }

export interface FeedbackRow {
  id: string; author_id: string; author_name?: string | null
  feedback_type: string; body: string | null
  original_score?: number | null; override_score?: number | null; override_reason?: string | null
  targets_lesson: boolean; lesson_unit?: number | null; lesson_number?: number | null
  visible_to_student: boolean; created_at: string
}

export interface RecordCourse {
  classId: string; className: string; subjectGroup: string; essayMode: string | null
  plan: PlanModule[]; planLessons: number; planConcepts: number
  progress: { currentUnit: number; currentLesson: number; totalCompleted: number }
  mastery: number; joinedAt: string | null; lastActiveAt: string | null
  lessons: RecordLesson[]; essays: EssayEvidence[]
  dateRange: { first: string | null; last: string | null }
  completedOn: string | null
}

export interface RecordChild {
  id: string
  name: string
  age: number | null
  facilityId?: string | null
  facilityType?: string | null
  orgType?: string | null
  orgName?: string | null
}

const DEMO_IDS = new Set(['00000000-0000-4000-a000-000000000a4a', 'demo-ava'])

export function useLearningRecord(childId?: string, classId?: string) {
  const { user, userProfile } = useAuth()
  const [child, setChild] = useState<RecordChild | null>(null)
  const [courses, setCourses] = useState<RecordCourse[]>([])
  const [feedback, setFeedback] = useState<FeedbackRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user || !childId) { setLoading(false); return }
    // Resolve the special "me" alias used by self-study learners navigating from /learn
    const resolvedChildId = childId === 'me' ? user.id : childId
    if (DEMO_IDS.has(resolvedChildId)) { setError('Sample data cannot be printed.'); setLoading(false); return }
    let cancelled = false

    async function load() {
      setLoading(true); setError(null)
      try {
        // ── 1. Child + ownership (mirrors ChildProgressPage's working guard;
        //        RLS does the real enforcement — this is UX-level only) ──
        let childRow: {
          id: string
          name: string | null
          age: number | null
          facility_id: string | null
          facilities?: { name?: string; facility_type?: string | null; branding?: any } | null
        } | null = null

        if (resolvedChildId === user!.id) {
          const { data } = await supabase
            .from('users')
            .select('id, name, age, facility_id, facilities(name, facility_type, branding)')
            .eq('id', user!.id)
            .maybeSingle()
          childRow = (data as any) ?? null
        } else {
          const { data } = await supabase
            .from('users')
            .select('id, name, age, facility_id, facility_role, facilities(name, facility_type, branding)')
            .eq('id', resolvedChildId)
            .maybeSingle()
          if (data && data.facility_id && data.facility_id === userProfile?.facility_id) childRow = (data as any)
        }
        if (!childRow) { if (!cancelled) setError('Record not found.'); return }

        // ── 2. Course discovery — mirrors useStudentProgress's invariant:
        //        visible = is_published AND (mode 'all' OR enrollment row).
        //        Broadcast courses NEVER carry enrollment rows (fan-out is
        //        progress-only), so enrollment-only discovery dropped them.
        const childFacilityId = childRow.facility_id
        const classSelect = 'id, name, subject_group, essay_mode, module_plan, is_published, enrollment_mode'

        // (a) Targeted/self-enrolled: classes the child is explicitly enrolled in
        let enrQuery = supabase.from('class_enrollments')
          .select(`class_id, classes(${classSelect})`)
          .eq('student_id', resolvedChildId)
        if (classId) enrQuery = enrQuery.eq('class_id', classId)
        const { data: enrData, error: enrErr } = await enrQuery
        if (enrErr) throw enrErr
        const enrRows = ((enrData || []).filter((e: any) => e.classes)) as any[]

        // (b) Broadcast: published 'all' classes in the child's facility
        let broadcastRows: any[] = []
        if (childFacilityId && !childFacilityId.startsWith('00000000-')) {
          let bQuery = (supabase.from('classes') as any)
            .select(classSelect)
            .eq('facility_id', childFacilityId)
            .eq('is_published', true)
            .eq('is_active', true)
            .eq('enrollment_mode', 'all')
          if (classId) bQuery = bQuery.eq('id', classId)
          const { data: bData, error: bErr } = await bQuery
          if (bErr) throw bErr
          broadcastRows = (bData || []).map((c: any) => ({ class_id: c.id, classes: c }))
        }

        // (c) Union + dedupe (unpublished classes are hidden in both modes)
        const seen = new Set<string>()
        const discovered: any[] = []
        for (const row of [...enrRows, ...broadcastRows]) {
          const cid = row.class_id
          if (!cid || seen.has(cid)) continue
          if (row.classes && row.classes.is_published === false) continue
          seen.add(cid)
          discovered.push(row)
        }

        // (d) Live stats live in student_subject_progress — class_enrollments
        //     rows only ever carry class_id/student_id.
        const { data: progRows, error: progErr } = await supabase
          .from('student_subject_progress')
          .select('class_id, mastery_score, current_unit, current_lesson, lessons_completed, started_at, last_activity_at')
          .eq('user_id', resolvedChildId)
        if (progErr) throw progErr
        const progByClass = new Map<string, any>()
        for (const p of (progRows || [])) {
          if (p.class_id && !progByClass.has(p.class_id)) progByClass.set(p.class_id, p)
        }

        // ── 3. Evidence — NO limits (records need everything) ──
        const [logsRes, quizRes, subRes, fbRes] = await Promise.all([
          supabase.from('student_lesson_logs')
            .select('class_id, unit_number, lesson_number, completed_at, metadata')
            .eq('user_id', resolvedChildId).order('completed_at', { ascending: true }),
          supabase.from('education_quiz_results')
            .select('class_id, lesson_unit, lesson_number, score_percentage, duration_seconds, attempted_at')
            .eq('user_id', resolvedChildId).order('attempted_at', { ascending: true }),
          supabase.from('university_submissions')
            .select('class_id, module_id, ai_score, max_score, ai_feedback, created_at')
            .eq('user_id', resolvedChildId).order('created_at', { ascending: true }),
          // ── Human feedback (teachers + parents) ──
          supabase.from('teacher_feedback' as any)
            .select('id, author_id, feedback_type, body, override_score, original_score, override_reason, targets_lesson, lesson_unit, lesson_number, visible_to_student, created_at')
            .eq('student_id', resolvedChildId)
            .eq('visible_to_student', true)
            .order('created_at', { ascending: true }),
        ])
        const logs = logsRes.data ?? []
        const quizzes = quizRes.data ?? []
        const submissions = subRes.data ?? []

        // Enrich feedback rows with author names (one batched lookup)
        const rawFeedback: FeedbackRow[] = (fbRes.data ?? []) as unknown as FeedbackRow[]
        if (rawFeedback.length > 0) {
          const authorIds = [...new Set(rawFeedback.map(f => f.author_id))]
          const { data: authors } = await supabase.from('users')
            .select('id, name')
            .in('id', authorIds)
          const nameMap = new Map((authors ?? []).map(a => [a.id, a.name]))
          rawFeedback.forEach(f => { f.author_name = nameMap.get(f.author_id) ?? null })
        }

        // ── 4. Evidence maps keyed by SEQUENTIAL positions (receipted convention:
        //        logs/quizzes carry sequential unit/lesson; plans carry hierarchical IDs — join by index) ──
        const quizMap = new Map<string, QuizEvidence>()
        for (const q of quizzes) {
          const k = `${q.class_id}:${q.lesson_unit}-${q.lesson_number}`
          const prev = quizMap.get(k)
          if (!prev) quizMap.set(k, { best: q.score_percentage, attempts: 1, lastAt: q.attempted_at, durationSeconds: q.duration_seconds })
          else {
            prev.best = Math.max(prev.best, q.score_percentage)
            prev.attempts += 1
            if (q.attempted_at && (!prev.lastAt || new Date(q.attempted_at) > new Date(prev.lastAt))) prev.lastAt = q.attempted_at
            prev.durationSeconds = (prev.durationSeconds ?? 0) + (q.duration_seconds ?? 0)
          }
        }
        const logMap = new Map<string, LogEvidence>()
        for (const l of logs) {
          const m = (l.metadata ?? {}) as { strong_areas?: string[]; weak_areas?: string[] }
          const k = `${l.class_id}:${l.unit_number}-${l.lesson_number}`
          const prev = logMap.get(k)
          if (!prev) logMap.set(k, { completedAt: l.completed_at, strongAreas: m.strong_areas ?? [], weakAreas: m.weak_areas ?? [] })
          else {
            if (l.completed_at && (!prev.completedAt || new Date(l.completed_at) > new Date(prev.completedAt))) prev.completedAt = l.completed_at
            if (m.strong_areas?.length) prev.strongAreas = m.strong_areas
            if (m.weak_areas?.length) prev.weakAreas = m.weak_areas
          }
        }

        // ── 5. Assemble courses ──
        const out: RecordCourse[] = []
        for (const e of discovered) {
          const cls = e.classes as any
          const plan: PlanModule[] = Array.isArray(cls?.module_plan) ? cls.module_plan : []
          if (plan.length === 0) continue   // guardrail: no syllabus → no record, never fabricate
          const prog = progByClass.get(e.class_id)
          const ps = {
            current_unit: prog?.current_unit ?? 1,
            current_lesson: prog?.current_lesson ?? 1,
            total_lessons_completed: Array.isArray(prog?.lessons_completed) ? prog.lessons_completed.length : 0,
          }

          const lessons: RecordLesson[] = []
          plan.forEach((m, mi) => {
            (m.lessons ?? []).forEach((l, li) => {
              const quiz = quizMap.get(`${e.class_id}:${mi + 1}-${li + 1}`)
              const log = logMap.get(`${e.class_id}:${mi + 1}-${li + 1}`)
              lessons.push({
                modulePos: mi + 1, moduleTitle: m.title, lessonPos: li + 1,
                title: l.title ?? `Lesson ${li + 1}`,
                objective: l.learning_objective ?? null,
                conceptCount: l.core_concepts?.length ?? 0,
                quiz, log,
                completed: !!log || (quiz?.best ?? 0) >= (l.passing_score ?? 80),
              })
            })
          })

          // Essays — dual-convention module join (grader write convention unverified; table empty)
          const essays: EssayEvidence[] = (cls?.essay_mode && cls.essay_mode !== 'none')
            ? submissions.filter(s => s.class_id === e.class_id).map(s => {
                const idx = plan.findIndex(m => Number(m.module_id) === Number(s.module_id))
                return { modulePos: idx >= 0 ? idx + 1 : Number(s.module_id), score: s.ai_score,
                         maxScore: s.max_score, feedback: s.ai_feedback, submittedAt: s.created_at }
              })
            : []

          const courseLogs = logs.filter(l => l.class_id === e.class_id)
          const first = courseLogs[0]?.completed_at ?? null
          const last = courseLogs[courseLogs.length - 1]?.completed_at ?? null
          const planLessons = plan.reduce((s, m) => s + (m.lessons?.length ?? 0), 0)
          const totalCompleted = ps.total_lessons_completed ?? 0

          out.push({
            classId: e.class_id, className: cls?.name || 'Course', subjectGroup: cls?.subject_group || 'General',
            essayMode: cls?.essay_mode ?? null, plan, planLessons,
            planConcepts: plan.reduce((s, m) => s + (m.lessons ?? []).reduce((x, l) => x + (l.core_concepts?.length ?? 0), 0), 0),
            progress: { currentUnit: ps.current_unit ?? 1, currentLesson: ps.current_lesson ?? 1, totalCompleted },
            mastery: Number(prog?.mastery_score) || 0,
            joinedAt: prog?.started_at ?? null, lastActiveAt: prog?.last_activity_at ?? null,
            lessons, essays, dateRange: { first, last },
            completedOn: planLessons > 0 && totalCompleted >= planLessons ? last : null,
          })
        }

        if (cancelled) return
        const childFacility = (childRow as any)?.facilities
        const childOrgType = childFacility?.branding?.org_type || (userProfile as any)?.facility_org_type || null
        const childFacilityType = childFacility?.facility_type || userProfile?.facility_type || null
        const childOrgName = childFacility?.branding?.display_name || childFacility?.name || null

        setChild({
          id: childRow.id,
          name: childRow.name || 'Student',
          age: childRow.age,
          facilityId: childFacilityId,
          facilityType: childFacilityType,
          orgType: childOrgType,
          orgName: childOrgName,
        })
        setCourses(out)
        setFeedback(rawFeedback)
      } catch (err) {
        if (!cancelled) setError((err as Error).message || 'Failed to load record.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [user?.id, userProfile?.facility_id, childId, classId])

  return { child, courses, feedback, loading, error }
}